'use client';

import { useId } from '@react-aria/utils';
import { useControlledState } from '@react-stately/utils';
import clsx from 'clsx';
import { type ForwardedRef, type KeyboardEvent, useImperativeHandle, useRef } from 'react';
import {
  type TextFieldProps as RACTextFieldProps,
  Group as RACGroup,
  Input as RACInput,
  TextField as RACTextField,
} from 'react-aria-components';
import { forwardRef } from '../../forward-ref.js';
import { useLocalizedMessages } from '../../i18n/localization-provider.js';
import { useProductionWarning } from '../../utils/use-production-warning.js';
import { withStyleProps } from '../../with-style-props.js';
import { FormFieldDescription, FormFieldError, FormFieldLabel } from '../internal/form-field/form-field.js';
import { XButton } from '../internal/x-button/x-button.js';
import type { AffixProps, ApiProps, CommonProps, FieldWithPlaceholderProps } from '../types.js';
import { textStyle } from '../typography/utils.js';

export interface TextFieldProps
  extends
    CommonProps,
    FieldWithPlaceholderProps<string>,
    AffixProps,
    ApiProps<TextFieldApi>,
    Pick<
      RACTextFieldProps,
      | 'value'
      | 'defaultValue'
      | 'onChange'
      | 'isDisabled'
      | 'isRequired'
      | 'isInvalid'
      | 'isReadOnly'
      | 'type'
      | 'autoFocus'
      | 'autoComplete'
      | 'inputMode'
      | 'minLength'
      | 'maxLength'
      | 'pattern'
      | 'onFocus'
      | 'onBlur'
      | 'onKeyDown'
    > {
  /**
   * Whether to render a button that can be used to clear the text field.
   * @default false
   */
  isClearable?: boolean;
}

/** The imperative API exposed by the `TextField` component. */
export interface TextFieldApi {
  /** Focuses the input field. */
  focus: () => void;
  /** Selects the contents of the input field. */
  select: () => void;
}

function TextField(
  {
    label,
    description,
    error: errorMessage,
    placeholder,
    prefix,
    suffix,
    UNSAFE_className,
    UNSAFE_style,
    isClearable = false,
    value: consumerValue,
    defaultValue: consumerDefaultValue,
    onChange,
    apiRef,
    ...props
  }: TextFieldProps,
  ref: ForwardedRef<HTMLDivElement>,
) {
  const { isDisabled, isReadOnly } = props;
  const inputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useControlledState(consumerValue, consumerDefaultValue ?? '', onChange);
  const messages = useLocalizedMessages('common');
  const clearDescriptionId = useId();

  useImperativeHandle(
    apiRef,
    () => ({
      focus: () => inputRef.current?.focus(),
      select: () => inputRef.current?.select(),
    }),
    [],
  );

  const { 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy } = props;

  const isFieldClearable = isClearable && !isDisabled && !isReadOnly;

  function onKeyDown(e: KeyboardEvent) {
    if (!isFieldClearable) {
      return;
    }

    if (e.key === 'Escape') {
      e.preventDefault();

      setValue('');
    }
  }

  useProductionWarning(() => {
    if (!label && !ariaLabel && !ariaLabelledBy) {
      console.warn('TextField requires one of label / aria-label / aria-labelledby for accessibility');
    }
  }, [label, ariaLabel, ariaLabelledBy]);

  return (
    <RACTextField
      {...props}
      ref={ref}
      className={clsx('cim-text-field', UNSAFE_className)}
      style={UNSAFE_style}
      value={value}
      onChange={setValue}
      aria-describedby={[props['aria-describedby'], isFieldClearable ? clearDescriptionId : undefined]
        .filter(Boolean)
        .join(' ')}
    >
      {({ isDisabled, isRequired }) => (
        <>
          <FormFieldLabel isRequired={isRequired} isDisabled={isDisabled}>
            {label}
          </FormFieldLabel>
          <RACGroup className="cim-text-field-input-group" onClick={() => inputRef.current?.focus()}>
            {prefix && (
              <div
                className={clsx(
                  'cim-text-field-affix',
                  textStyle({ variant: 'body', tone: isDisabled ? 'muted' : 'subtle' }),
                )}
                aria-hidden
              >
                {prefix}
              </div>
            )}

            <RACInput
              ref={inputRef}
              className={clsx('cim-text-field-input', textStyle({ variant: 'body' }))}
              placeholder={placeholder}
              onKeyDown={onKeyDown}
            />

            {isFieldClearable && value !== '' && (
              <XButton
                slot={null}
                UNSAFE_className="cim-text-field-clear"
                aria-label={messages.format('clearValue')}
                onPress={() => setValue('')}
                iconType="circled"
                excludeFromTabOrder
              />
            )}

            {suffix && (
              <div
                className={clsx(
                  'cim-text-field-affix',
                  textStyle({ variant: 'body', tone: isDisabled ? 'muted' : 'subtle' }),
                )}
                aria-hidden
              >
                {suffix}
              </div>
            )}
          </RACGroup>
          <FormFieldError>{errorMessage}</FormFieldError>
          <FormFieldDescription>{description}</FormFieldDescription>
          {isFieldClearable && (
            <p id={clearDescriptionId} className="cim-text-field-clear-description" hidden>
              {messages.format('clearValueDescription')}
            </p>
          )}
        </>
      )}
    </RACTextField>
  );
}

/**
 * Allows users to enter a single line of text with a keyboard.
 *
 * See [text field usage guidelines](https://ui.cimpress.io/components/text-field/).
 */
const _TextField = withStyleProps(forwardRef(TextField), 'TextField');

export { _TextField as TextField };
